import type { Cosmetics } from "@neon-purge/shared";

export interface Account {
  id: string;
  email: string;
  username: string;
  cosmetics: Required<Cosmetics>;
  settings?: Record<string, unknown>;
  /** Custom skin texture (data URL), set via the skin editor. */
  skinUrl?: string;
  createdAt: string;
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = "neonpurge.tokens";

/** REST client for the auth/profile API. Persists tokens, auto-refreshes on 401. */
export class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  account: Account | null = null;
  /** Guest session: token lives only in memory (a reload = a fresh guest),
   *  and cosmetics/skins apply locally instead of persisting. */
  guest = false;
  private guestSkins: { id: string; name: string; data: string }[] = [];

  constructor() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const t = JSON.parse(raw) as TokenPair;
        this.accessToken = t.accessToken;
        this.refreshToken = t.refreshToken;
      }
    } catch {
      /* corrupted storage — start logged out */
    }
  }

  get token(): string | null {
    return this.accessToken;
  }

  private saveTokens(t: TokenPair | null): void {
    this.accessToken = t?.accessToken ?? null;
    this.refreshToken = t?.refreshToken ?? null;
    if (t) localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    else localStorage.removeItem(STORAGE_KEY);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    retry = true,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["content-type"] = "application/json";
    if (this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
    const res = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401 && retry && this.refreshToken) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.request<T>(method, path, body, false);
    }
    const data = (await res.json().catch(() => ({}))) as T & { error?: string };
    if (!res.ok) throw new Error(data.error ?? `${res.status} ${res.statusText}`);
    return data;
  }

  /** Single-flight: concurrent 401s must share ONE refresh — two parallel
   *  refreshes with a rotated (single-use) token log the user out. */
  private refreshing: Promise<boolean> | null = null;

  private tryRefresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const res = await fetch("/api/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });
      if (!res.ok) {
        // clear only on a real rejection — a 5xx/network blip is not "logged out"
        if (res.status === 401 || res.status === 403) this.saveTokens(null);
        return false;
      }
      const data = (await res.json()) as TokenPair & { account: Account };
      this.saveTokens(data);
      this.account = data.account;
      this.lastRefreshAt = Date.now();
      return true;
    } catch {
      return false;
    }
  }

  private lastRefreshAt = 0;

  /** Make sure the access token is fresh enough for a gameserver join
   *  (access tokens live 15 min; joins bypass the REST auto-refresh).
   *  Skips the round trip when the token is under 8 minutes old, so join
   *  clicks aren't taxed with an extra request. */
  async ensureFreshToken(): Promise<void> {
    if (this.guest || !this.refreshToken) return; // guests: long-lived token
    if (Date.now() - this.lastRefreshAt < 8 * 60_000) return;
    await this.tryRefresh();
  }

  async register(email: string, username: string, password: string): Promise<Account> {
    const data = await this.request<TokenPair & { account: Account }>("POST", "/register", {
      email,
      username,
      password,
    });
    this.saveTokens(data);
    applySyncedSettings(data.account.settings);
    this.account = data.account;
    return data.account;
  }

  async login(identifier: string, password: string): Promise<Account> {
    const data = await this.request<TokenPair & { account: Account }>("POST", "/login", {
      identifier,
      password,
    });
    this.saveTokens(data);
    applySyncedSettings(data.account.settings);
    this.account = data.account;
    this.lastRefreshAt = Date.now();
    return data.account;
  }

  /** Anonymous play: server mints a throwaway identity, nothing persists. */
  async loginAsGuest(): Promise<Account> {
    const data = await this.request<TokenPair & { account: Account }>("POST", "/guest");
    // memory only — never written to localStorage
    this.accessToken = data.accessToken;
    this.refreshToken = null;
    this.account = data.account;
    this.guest = true;
    return data.account;
  }

  /** Restore a session from stored tokens. Returns null if logged out. */
  async restore(): Promise<Account | null> {
    if (!this.accessToken && !this.refreshToken) return null;
    try {
      const { account } = await this.request<{ account: Account }>("GET", "/me");
      applySyncedSettings(account.settings);
      this.account = account;
      return account;
    } catch (err) {
      // keep the tokens on NETWORK failures (offline, API restarting) — only
      // a real auth rejection means the session is dead. fetch() rejections
      // surface as TypeError; our HTTP errors carry the status text.
      if (err instanceof TypeError) return null;
      this.saveTokens(null);
      return null;
    }
  }

  async patchCosmetics(patch: Cosmetics): Promise<Account> {
    if (this.guest && this.account) {
      // local-only: visible in-game (cosmetics ride the room-join options)
      this.account.cosmetics = { ...this.account.cosmetics, ...patch };
      return this.account;
    }
    const { account } = await this.request<{ account: Account }>(
      "PATCH",
      "/me/cosmetics",
      patch,
    );
    this.account = account;
    return account;
  }

  /** Save (or clear with null) the custom character skin. */
  /** Persist gameplay settings (sensitivity/vibration/volumes) to the account
   *  — fire-and-forget; guests just keep localStorage. */
  /** Owner-only: the admin connection log. */
  adminConnections(): Promise<{ connections: unknown[] }> {
    return this.request("GET", "/admin/connections");
  }

  saveSettings(patch: Record<string, unknown>): void {
    if (!this.account) return;
    void this.request("PATCH", "/me/settings", patch).catch(() => { /* guest or offline */ });
  }

  async patchSkin(skin: string | null): Promise<Account> {
    if (this.guest && this.account) {
      this.account.skinUrl = skin ?? undefined;
      return this.account;
    }
    const { account } = await this.request<{ account: Account }>("PATCH", "/me/skin", { skin });
    this.account = account;
    return account;
  }

  async getStats(): Promise<Record<string, number>> {
    const { stats } = await this.request<{ stats: Record<string, number> }>("GET", "/me/stats");
    return stats;
  }

  /** Lifetime stats plus the derived progression rank (name/colour/level). */
  async getStatsAndRank(): Promise<{
    stats: Record<string, number>;
    rank?: { name: string; color: string; level: number; frac: number };
  }> {
    return this.request<{
      stats: Record<string, number>;
      rank?: { name: string; color: string; level: number; frac: number };
    }>("GET", "/me/stats");
  }

  async getLeaderboard(): Promise<
    { username: string; kills: number; deaths: number; wins: number; matches: number; level?: number; rank?: string }[]
  > {
    const { leaderboard } = await this.request<{
      leaderboard: { username: string; kills: number; deaths: number; wins: number; matches: number }[];
    }>("GET", "/leaderboard");
    return leaderboard;
  }

  async listSkins(): Promise<{ id: string; name: string; data: string }[]> {
    if (this.guest) return this.guestSkins;
    const { skins } = await this.request<{ skins: { id: string; name: string; data: string }[] }>("GET", "/me/skins");
    return skins;
  }

  async saveSkin(name: string, data: string): Promise<{ id: string; name: string }> {
    if (this.guest) {
      const skin = { id: `local-${this.guestSkins.length + 1}`, name, data };
      this.guestSkins.unshift(skin);
      return skin;
    }
    const { skin } = await this.request<{ skin: { id: string; name: string } }>("POST", "/me/skins", { name, data });
    return skin;
  }

  async equipSkin(id: string): Promise<void> {
    if (this.guest) {
      const skin = this.guestSkins.find((s) => s.id === id);
      if (skin && this.account) this.account.skinUrl = skin.data;
      return;
    }
    const { account } = await this.request<{ account: Account }>("POST", `/me/skins/${id}/equip`);
    this.account = account;
  }

  // ---- community custom maps (build / publish / browse) ----
  async saveMap(name: string, data: unknown, id?: string): Promise<{ id: string; name: string; published: boolean }> {
    const { map } = await this.request<{ map: { id: string; name: string; published: boolean } }>(
      "POST", "/maps", { name, data, ...(id ? { id } : {}) });
    return map;
  }

  async publishMap(id: string, published = true): Promise<{ id: string; name: string; published: boolean }> {
    const { map } = await this.request<{ map: { id: string; name: string; published: boolean } }>(
      "POST", `/maps/${id}/publish`, { published });
    return map;
  }

  async deleteMap(id: string): Promise<void> {
    await this.request("DELETE", `/maps/${id}`);
  }

  async listMyMaps(): Promise<{ id: string; name: string; published: boolean; updated_at: string }[]> {
    const { maps } = await this.request<{ maps: { id: string; name: string; published: boolean; updated_at: string }[] }>("GET", "/maps/mine");
    return maps;
  }

  async listPublishedMaps(): Promise<{ id: string; name: string; author: string; updated_at: string }[]> {
    const { maps } = await this.request<{ maps: { id: string; name: string; author: string; updated_at: string }[] }>("GET", "/maps/published");
    return maps;
  }

  async getMap(id: string): Promise<{ id: string; name: string; author: string; published: boolean; data: unknown }> {
    const { map } = await this.request<{ map: { id: string; name: string; author: string; published: boolean; data: unknown } }>("GET", `/maps/${id}`);
    return map;
  }

  async deleteSkin(id: string): Promise<void> {
    if (this.guest) {
      this.guestSkins = this.guestSkins.filter((s) => s.id !== id);
      return;
    }
    await this.request("DELETE", `/me/skins/${id}`);
  }

  async logout(): Promise<void> {
    this.guest = false;
    this.guestSkins = [];
    if (this.refreshToken) {
      await fetch("/api/logout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      }).catch(() => {});
    }
    this.saveTokens(null);
    this.account = null;
  }
}

/** Pour the account's synced settings into the localStorage-backed stores so
 *  every device follows the user's tuning. Local values are the fallback. */
function applySyncedSettings(settings?: Record<string, unknown>): void {
  if (!settings) return;
  try {
    if (settings.sensitivity) localStorage.setItem("neonpurge.sensitivity", JSON.stringify(settings.sensitivity));
    if (settings.behavior) localStorage.setItem("neonpurge.behavior", JSON.stringify(settings.behavior));
    if (settings.volumes) localStorage.setItem("neonpurge.volumes", JSON.stringify(settings.volumes));
    if (settings.soundVols) localStorage.setItem("neonpurge.soundvols", JSON.stringify(settings.soundVols));
  } catch { /* storage unavailable */ }
  // the audio engine booted BEFORE login pulled these — tell it to re-read
  // (event keeps api.ts decoupled from the audio module)
  window.dispatchEvent(new Event("np-settings-synced"));
}

export const api = new ApiClient();
